import {
  ControlValueAccessor,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms'

const noop = (): void => {} // eslint-disable-line

/**
 * Factory for providers to mark the component as a ng_value_accessor
 * use like this :
 * ```ts
 * @Component({
 *  providers:[CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR(() => ComponnentClass)]
 * })
 * ```
export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = function <T>(comp: Type<T>) {
  return {
    provide: NG_VALUE_ACCESSOR,
    useExisting: comp,
    multi: true
  };
}

 */
export abstract class NewCustomFormControl
  implements ControlValueAccessor, Validator
{
  /** Whether or not the form is disabled */
  public isDisabled: boolean

  /** The value */
  protected _value: any

  /**  Placeholders for the callbacks which are later provided
   * by the Control Value Accessor **/
  protected _onTouchedCallback: () => void = noop

  /**  Placeholders for the callbacks which are later provided
   * by the Control Value Accessor **/
  protected _onChangedCallback: (value: any) => void = noop

  constructor(public ngControl: NgControl) {
    if (ngControl !== null) {
      ngControl.valueAccessor = this
    }
  }

  /** Getter for the value
   * @returns { any }
   */
  public get value(): any {
    return this._value
  }

  /** Setter for the value
   * @param { any } value
   */
  public setValue(value: any, noEvent = false) {
    this._value = value
    if (!noEvent) {
      this._onChangedCallback(this.value)
    }
  }

  /**  Set touched on blur **/
  public onBlur(): void {
    this._onTouchedCallback()
  }

  /**  Set touched on blur **/
  public onChange(value: any): void {
    this._onChangedCallback(value)
  }

  /** Writes the value in the form
   * @param { any } value the value typed
   */
  public writeValue(value: any): void {
    this.setValue(value, true)
    this._onTouchedCallback()
  }

  /** Register the change of the form */
  public registerOnChange(fn: any): void {
    this._onChangedCallback = fn
  }

  /** Register the touch callback */
  public registerOnTouched(fn: any): void {
    this._onTouchedCallback = fn
  }

  /** Register the changes of validator */
  public registerOnValidatorChange(fn: () => void): void {
    this.ngControl.statusChanges?.subscribe(fn)
  }

  /** Sets the disabled state to the control
   * @param { boolean } isDisabled whether or not the control is disabled
   */
  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled
  }

  /** Validates the form and returns an error if unvalid
   * @return { ValidationErrors | null }
   */
  public validate(): ValidationErrors | null {
    return this.ngControl.valid
      ? null
      : { customNestedFormControlError: 'Error in custom nested form' }
  }
}
